import React, { useState } from 'react';

import { ExclamationOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Box, Grid, Switch, Text } from '@lp/ds-next';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import {
  Tooltip,
  InputNumber,
  Button,
  Form,
  Space,
  Input,
  Modal,
  Typography,
} from 'antd';

import { IPartnerV3, IPartnerV3Update } from '../../../models/partnerV3';
import { useGetPartnerHierarchy } from '@/features/lp-village/partners/api/getPartnerHierarchy';

const { Text: AntText } = Typography;

export const Toggles = ({
  partner,
  updatePartner,
}: {
  partner: IPartnerV3;
  updatePartner: (data: IPartnerV3Update) => Promise<{ success: boolean }>;
}) => {
  const [daysValue, setDaysValue] = useState<number | null>(
    partner.days_before_shipment_anonymization ?? null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [ssoConnectionName, setSsoConnectionName] = useState<string>(
    partner.sso_connection_name || ''
  );
  const [isSavingSso, setIsSavingSso] = useState(false);

  // Domain hook — React Query cache shared with OrganizationTab (no extra network call)
  const { data: hierarchy } = useGetPartnerHierarchy(partner.id, {
    enabled: !!partner.id && !partner.parent_id,
  });
  const hasChildren = (hierarchy?.children?.length ?? 0) > 0;

  const handleSaveSsoConnectionName = async () => {
    if (ssoConnectionName === partner.sso_connection_name) return;
    setIsSavingSso(true);
    try {
      await updatePartner({
        sso_connection_name:
          ssoConnectionName.trim() === ''
            ? undefined
            : ssoConnectionName.trim(),
      });
    } finally {
      setIsSavingSso(false);
    }
  };

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    // Guard: block disabling Master Account when child organizations exist
    if (name === 'master_account_enabled' && !checked && hasChildren) {
      Modal.warning({
        title: 'Cannot disable Master Account',
        icon: <ExclamationOutlined />,
        content: `This partner has ${hierarchy?.children?.length} organization${(hierarchy?.children?.length ?? 0) > 1 ? 's' : ''}. Remove all child organizations before disabling Master Account.`,
        okText: 'ok',
        okButtonProps: {
          style: {
            backgroundColor: '#00BF6F',
            borderColor: '#00BF6F',
          },
        },
      });

      return;
    }

    // Confirmation: disabling Master Account (no children)
    if (name === 'master_account_enabled' && !checked) {
      Modal.confirm({
        title: 'Disable Master Account',
        icon: <ExclamationOutlined />,
        content:
          'Are you sure you want to disable the Master Account feature on this partner?',
        okText: 'Disable',
        okButtonProps: { danger: true },
        onOk: () => updatePartner({ master_account_enabled: false }),
      });

      return;
    }

    // Special handling: when disabling SSO we also clear stored connection name
    if (name === 'sso_enabled' && !checked) {
      setSsoConnectionName('');
      await updatePartner({
        sso_enabled: false,
        sso_connection_name: '',
      });

      return;
    }

    await updatePartner({ [name]: checked });
  };

  const handleDaysSubmit = async () => {
    if (daysValue === null) return;

    setIsLoading(true);
    try {
      await updatePartner({
        days_before_shipment_anonymization: daysValue,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid item mobile={12} tablet={4}>
      <FormControl component="fieldset" variant="standard">
        <FormLabel component="legend">
          <Text variant="titleL">Miscellaneous Controls</Text>
        </FormLabel>
        <FormGroup sx={{ mt: '1rem', gap: '0.5rem' }}>
          <Box>
            <Switch
              label="Code on the BOX"
              checked={partner.code_opening_enabled}
              onChange={onChange}
              name="code_opening_enabled"
            />
          </Box>

          <Box>
            <Switch
              label="Delivery key via SMS"
              checked={partner.sms_enabled}
              onChange={onChange}
              name="sms_enabled"
            />
          </Box>

          <Box>
            <Switch
              label="Remote control"
              checked={partner.remote_control_enabled}
              onChange={onChange}
              name="remote_control_enabled"
            />
          </Box>

          <Box>
            <Switch
              label="Update firmware"
              checked={partner.fw_update_enabled}
              onChange={onChange}
              name="fw_update_enabled"
            />
          </Box>

          <Box>
            <Switch
              label="Flight mode"
              checked={partner.flight_mode_enabled}
              onChange={onChange}
              name="flight_mode_enabled"
            />
          </Box>
          <Box display="flex" flexDirection="row" gap={1}>
            <Switch
              label="Service type"
              checked={partner.service_type_enabled}
              onChange={onChange}
              name="service_type_enabled"
            />
            <Tooltip
              title={
                <Box>
                  {partner.service_types?.map((serviceType) => (
                    <Text key={serviceType.id}>
                      {serviceType.id} - {serviceType.name}
                    </Text>
                  ))}
                </Box>
              }
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </Box>
          <Box display="flex" flexDirection="row" gap={1}>
            <Switch
              label="Master account"
              checked={partner.master_account_enabled}
              onChange={onChange}
              name="master_account_enabled"
              disabled={!!partner.parent_id}
            />
            {partner.parent_id && (
              <Tooltip title="This partner is an organization (has a parent). Master account cannot be enabled on organizations.">
                <QuestionCircleOutlined />
              </Tooltip>
            )}
            {hasChildren && (
              <Tooltip title="Master Account cannot be disabled while child organizations exist. Remove all organizations first.">
                <QuestionCircleOutlined />
              </Tooltip>
            )}
          </Box>

          <Box sx={{ mt: '1rem' }}>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ mb: '0.5rem' }}
            >
              <Text variant="titleS">Days before shipment anonymization</Text>
              <Tooltip title="Set to 0 to anonymize customer data only after account closure. Set a number of days to enable rolling anonymization after delivery.">
                <QuestionCircleOutlined />
              </Tooltip>
            </Box>
            <Form layout="vertical">
              <Space.Compact style={{ display: 'flex', width: '100%' }}>
                <InputNumber
                  placeholder="Enter number of days"
                  data-testid="days-anonymization-input"
                  value={daysValue}
                  onChange={setDaysValue}
                  min={0}
                  max={365}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  onClick={handleDaysSubmit}
                  data-testid="save-days-button"
                  loading={isLoading}
                  disabled={daysValue === null}
                >
                  Update
                </Button>
              </Space.Compact>
            </Form>
          </Box>

          <Box display="flex" alignItems="center" gap={4} marginTop={2}>
            <Switch
              label="SSO"
              checked={partner.sso_enabled}
              onChange={onChange}
              name="sso_enabled"
            />
            <Tooltip title="Activating SSO requires adding the associated Auth0 connection name (must be created on Auth0 by an admin).">
              <QuestionCircleOutlined style={{ fontSize: 18, color: '#555' }} />
            </Tooltip>
          </Box>
          {partner.sso_enabled && (
            <Box>
              <Form layout="vertical">
                <Form.Item
                  label="SSO Connection Name (comma separated values)"
                  style={{ marginBottom: 0 }}
                >
                  <Space style={{ width: '100%' }} size="small">
                    <Input.TextArea
                      placeholder="SSO connection name"
                      value={ssoConnectionName}
                      autoSize={{ minRows: 2, maxRows: 4 }}
                      onChange={(e) => setSsoConnectionName(e.target.value)}
                    />
                    <Button
                      type="primary"
                      size="small"
                      onClick={handleSaveSsoConnectionName}
                      loading={isSavingSso}
                      disabled={
                        ssoConnectionName.trim() === '' ||
                        ssoConnectionName === partner.sso_connection_name
                      }
                    >
                      Save
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Box>
          )}
          <Box display="flex" flexDirection="row" gap={1}>
            <Switch
              label="Map Widget"
              checked={partner.widget_enabled}
              onChange={onChange}
              name="widget_enabled"
            />
            <Tooltip
              title={
                <Box>
                  <AntText strong style={{ color: 'white' }}>
                    Enabling this option allows the partner to use the Map
                    Widget, which provides :
                    <ul>
                      <li>- Tracking</li>
                      <li>- BOX location</li>
                      <li>- Alerts visualization</li>
                    </ul>
                  </AntText>
                  <br />
                  {/* Link to documentation to provide to the client */}
                  <AntText style={{ color: 'white' }}>
                    <AntText strong style={{ color: 'white' }}>
                      Integration guide available here:{' '}
                    </AntText>
                    <a
                      href="https://livingpackets.com/docs/map-widget"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'white',
                        textDecoration: 'underline',
                      }}
                    >
                      add real link
                    </a>
                  </AntText>
                </Box>
              }
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </Box>
        </FormGroup>
      </FormControl>
    </Grid>
  );
};
